import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import 'leaflet/dist/leaflet.css';
import * as Sentry from '@sentry/react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { supabaseIntegration } from '@supabase/sentry-js-integration';
import { isAllowedDeepLinkPath, normalizeDeepLinkPath } from '@/lib/deepLinkWhitelist';
import { filterSentryEvent } from '@/utils/sentryBeforeSend';
import './utils/globalErrorLogger';

const isProduction =
  import.meta.env.MODE === 'production' || process.env.NODE_ENV === 'production';

const replaySessionSampleRate = isProduction ? 0.1 : 1.0;
const replayErrorSampleRate = 1.0;

if (isProduction) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || process.env.VITE_SENTRY_DSN,
    integrations: [
      supabaseIntegration(supabase, Sentry, {
        tracing: true,
        breadcrumbs: true,
        errors: true,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: replaySessionSampleRate,
    replaysOnErrorSampleRate: replayErrorSampleRate,
    beforeSend: filterSentryEvent,
  });
}
// Handle deep links (App Links https://www.ceralia.it/...)
if (Capacitor.isPluginAvailable('App')) {
  CapacitorApp.addListener('appUrlOpen', (data) => {
    const url = data.url;
    if (url) {
      try {
        const { pathname, search, hash } = new URL(url);
        // Whitelist dei path: non inoltriamo path arbitrari (hardening deep-link).
        if (!isAllowedDeepLinkPath(pathname)) {
          console.warn('Deep link verso path non consentito, ignorato', pathname);
          return;
        }
        const safePath = normalizeDeepLinkPath(pathname);
        // include search/hash per preservare gli eventuali parametri auth (es. ?code=)
        window.location.replace(safePath + search + hash);
      } catch {
        console.warn('Invalid app URL received', url);
      }
    }
  });
} else {
  console.warn('Capacitor App plugin not available; appUrlOpen listener not registered');
}
// Ensure the app root is interactive even if external scripts mark it as inert
const rootElement = document.getElementById('root') as (HTMLElement & { inert?: boolean }) | null;

if (rootElement) {
  const ensureInteractive = () => {
    rootElement.removeAttribute('aria-hidden');
    rootElement.inert = false;
  };

  // run immediately in case attributes are already set
  ensureInteractive();

  // watch for external scripts reapplying the attributes (e.g. gptengineer)
  const observer = new MutationObserver(() => ensureInteractive());
  observer.observe(rootElement, {
    attributes: true,
    attributeFilter: ['aria-hidden', 'inert'],
  });

  createRoot(rootElement).render(<App />);
}
